import { useState, useEffect, useRef, useCallback } from "react";

const API_URL = "https://weather-backend.weatherwalay-dashboard.workers.dev/stats";
const POLL_INTERVAL = 15 * 60 * 1000;

export default function WeatherDashboard() {
  const [stations, setStations] = useState([]);
  const [lastFetched, setLastFetched] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("uptime");
  const [activeTab, setActiveTab] = useState("monitored");
  const timerRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(API_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setStations(json.stations || []);
      setLastFetched(new Date());
      setError(null);
      setLoading(false);
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    timerRef.current = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [fetchData]);

  const monitoredStations = stations
    .filter(s => s.status !== "Disabled")
    .filter(s => {
      const q = search.toLowerCase();
      return (
        s.poi?.toLowerCase().includes(q) ||
        s.stationID?.toString().includes(q) ||
        s.ownedBy?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      if (sortBy === "uptime") return (b.uptime ?? -1) - (a.uptime ?? -1);
      if (sortBy === "name") return (a.poi || "").localeCompare(b.poi || "");
      if (sortBy === "wind") return (b.maxWind ?? -1) - (a.maxWind ?? -1);
      if (sortBy === "temp") return (b.maxTemp ?? -1) - (a.maxTemp ?? -1);
      if (sortBy === "rain") return (b.maxRain ?? -1) - (a.maxRain ?? -1);
      return 0;
    });

  const disabledStations = stations
    .filter(s => s.status === "Disabled")
    .filter(s => {
      const q = search.toLowerCase();
      return (
        s.poi?.toLowerCase().includes(q) ||
        s.stationID?.toString().includes(q) ||
        s.ownedBy?.toLowerCase().includes(q)
      );
    });

  const activeNow = stations.filter(s => s.status === "Active").length;
  const inactiveNow = stations.filter(s => s.status === "Inactive").length;
  const disabledCount = stations.filter(s => s.status === "Disabled").length;

  const [countdown, setCountdown] = useState(POLL_INTERVAL / 1000);
  const countRef = useRef(POLL_INTERVAL / 1000);
  useEffect(() => {
    const tick = setInterval(() => {
      countRef.current = Math.max(0, countRef.current - 1);
      if (countRef.current === 0) countRef.current = POLL_INTERVAL / 1000;
      setCountdown(countRef.current);
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  const fmtCountdown = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const fmt = v => (v !== null && v !== undefined ? v : "—");

  const displayList = activeTab === "monitored" ? monitoredStations : disabledStations;

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <div>
          <div style={styles.logo}>WeatherWalay</div>
          <div style={styles.subtitle}>Station Operations Dashboard</div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.pill}><span style={{ ...styles.dot, background: "#4ade80" }} />{activeNow} Active</div>
          <div style={styles.pill}><span style={{ ...styles.dot, background: "#f87171" }} />{inactiveNow} Inactive</div>
          <div style={styles.pill}><span style={{ ...styles.dot, background: "#64748b" }} />{disabledCount} Disabled</div>
          <div style={styles.pill}>⏱ Next poll in {fmtCountdown(countdown)}</div>
          {lastFetched && <div style={styles.pillMuted}>Last updated {lastFetched.toLocaleTimeString()}</div>}
        </div>
      </div>

      {!loading && !error && (
        <div style={styles.summaryBar}>
          {[
            { label: "Total stations", value: stations.length, icon: "📡" },
            { label: "Active now", value: activeNow, icon: "✅" },
            { label: "Inactive now", value: inactiveNow, icon: "⚠️" },
            { label: "Disabled", value: disabledCount, icon: "🔕" },
            {
              label: "Avg uptime today", icon: "📊",
              value: (() => {
                const vals = monitoredStations.map(s => s.uptime).filter(v => v !== null);
                return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) + "%" : "—";
              })(),
            },
          ].map(item => (
            <div key={item.label} style={styles.summaryCard}>
              <div style={styles.summaryIcon}>{item.icon}</div>
              <div style={styles.summaryValue}>{item.value}</div>
              <div style={styles.summaryLabel}>{item.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={styles.tabBar}>
        <button style={{ ...styles.tab, ...(activeTab === "monitored" ? styles.tabActive : {}) }} onClick={() => setActiveTab("monitored")}>
          Monitored Stations
          <span style={{ ...styles.tabCount, background: activeTab === "monitored" ? "rgba(99,102,241,0.3)" : "rgba(255,255,255,0.08)" }}>
            {stations.filter(s => s.status !== "Disabled").length}
          </span>
        </button>
        <button style={{ ...styles.tab, ...(activeTab === "disabled" ? styles.tabActive : {}) }} onClick={() => setActiveTab("disabled")}>
          Disabled Stations
          <span style={{ ...styles.tabCount, background: activeTab === "disabled" ? "rgba(99,102,241,0.3)" : "rgba(255,255,255,0.08)" }}>
            {disabledCount}
          </span>
        </button>
      </div>

      <div style={styles.controls}>
        <input
          style={styles.search}
          placeholder="Search by station name or ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {activeTab === "monitored" && (
          <div style={styles.sortGroup}>
            <span style={styles.sortLabel}>Sort by</span>
            {[
              { key: "uptime", label: "Uptime" },
              { key: "name", label: "Name" },
              { key: "wind", label: "Wind" },
              { key: "temp", label: "Temp" },
              { key: "rain", label: "Rain" },
            ].map(opt => (
              <button
                key={opt.key}
                style={{ ...styles.sortBtn, ...(sortBy === opt.key ? styles.sortBtnActive : {}) }}
                onClick={() => setSortBy(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {activeTab === "disabled" && !loading && (
        <div style={styles.infoBox}>
          🔕 These stations are marked Disabled in the system and excluded from all uptime calculations.
        </div>
      )}

      {loading && <div style={styles.centered}><div style={styles.loadingText}>Loading stations…</div></div>}
      {error && (
        <div style={styles.errorBox}>
          ⚠️ Could not fetch data: {error}
          <button style={styles.retryBtn} onClick={fetchData}>Retry</button>
        </div>
      )}

      {!loading && !error && (
        <div style={styles.tableWrap}>
          {displayList.length === 0 ? (
            <div style={styles.emptyState}>
              {activeTab === "disabled" ? "No disabled stations." : "No stations match your search."}
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {(activeTab === "monitored"
                    ? ["Station", "ID", "Owner", "Status", "Uptime today", "Readings", "Max wind (km/h)", "Max temp (°C)", "Min temp (°C)", "Max rain (mm)"]
                    : ["Station", "ID", "Owner", "Status"]
                  ).map(h => <th key={h} style={styles.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {displayList.map((s, i) => {
                  const isActive = s.status === "Active";
                  return (
                    <tr key={s.stationID} style={{ background: i % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent" }}>
                      <td style={styles.td}><span style={styles.stationName}>{s.poi}</span></td>
                      <td style={{ ...styles.td, color: "#94a3b8", fontFamily: "monospace" }}>{s.stationID}</td>
                      <td style={styles.td}>{s.ownedBy || "—"}</td>

                      {activeTab === "monitored" ? (
                        <>
                          <td style={styles.td}>
                            <span style={{ ...styles.statusBadge, background: isActive ? "#14532d" : "#3b1111", color: isActive ? "#4ade80" : "#f87171", border: `1px solid ${isActive ? "#166534" : "#7f1d1d"}` }}>
                              {isActive ? "● Active" : "○ Inactive"}
                            </span>
                          </td>
                          <td style={styles.td}>
                            {s.uptime !== null ? (
                              <div style={styles.uptimeWrap}>
                                <div style={styles.uptimeBar}>
                                  <div style={{ ...styles.uptimeFill, width: `${s.uptime}%`, background: s.uptime >= 75 ? "#4ade80" : s.uptime >= 50 ? "#facc15" : "#f87171" }} />
                                </div>
                                <span style={{ fontSize: 12, color: s.uptime >= 75 ? "#4ade80" : s.uptime >= 50 ? "#facc15" : "#f87171" }}>{s.uptime}%</span>
                              </div>
                            ) : (
                              <span style={{ color: "#64748b", fontSize: 12 }}>no data yet</span>
                            )}
                          </td>
                          <td style={{ ...styles.td, textAlign: "center", color: "#64748b" }}>{s.readingCount}</td>
                          <td style={styles.td}><span style={styles.stat}>{fmt(s.maxWind)}</span></td>
                          <td style={styles.td}><span style={{ ...styles.stat, color: "#fb923c" }}>{fmt(s.maxTemp)}</span></td>
                          <td style={styles.td}><span style={{ ...styles.stat, color: "#60a5fa" }}>{fmt(s.minTemp)}</span></td>
                          <td style={styles.td}><span style={{ ...styles.stat, color: "#818cf8" }}>{fmt(s.maxRain)}</span></td>
                        </>
                      ) : (
                        <td style={styles.td}>
                          <span style={{ ...styles.statusBadge, background: "#1e1b4b", color: "#a5b4fc", border: "1px solid #3730a3" }}>
                            ⊘ Disabled by system
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  root: { minHeight: "100vh", background: "#0a0f1a", color: "#e2e8f0", fontFamily: "'DM Sans', 'Segoe UI', sans-serif", padding: "0 0 60px" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "28px 32px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", flexWrap: "wrap", gap: 16 },
  logo: { fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", color: "#f1f5f9" },
  subtitle: { fontSize: 13, color: "#64748b", marginTop: 3 },
  headerRight: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  pill: { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, padding: "5px 14px", fontSize: 13, display: "flex", alignItems: "center", gap: 6 },
  pillMuted: { fontSize: 12, color: "#475569" },
  dot: { width: 7, height: 7, borderRadius: "50%", display: "inline-block" },
  summaryBar: { display: "flex", gap: 16, padding: "20px 32px", borderBottom: "1px solid rgba(255,255,255,0.06)", flexWrap: "wrap" },
  summaryCard: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "14px 22px", minWidth: 120, display: "flex", flexDirection: "column", gap: 4 },
  summaryIcon: { fontSize: 18 },
  summaryValue: { fontSize: 24, fontWeight: 700, color: "#f1f5f9" },
  summaryLabel: { fontSize: 12, color: "#64748b" },
  tabBar: { display: "flex", gap: 4, padding: "16px 32px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" },
  tab: { background: "transparent", border: "none", borderBottom: "2px solid transparent", color: "#64748b", padding: "8px 16px 12px", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 },
  tabActive: { color: "#a5b4fc", borderBottom: "2px solid #6366f1" },
  tabCount: { borderRadius: 20, padding: "1px 8px", fontSize: 11 },
  controls: { display: "flex", gap: 16, padding: "16px 32px", alignItems: "center", flexWrap: "wrap" },
  search: { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "8px 14px", color: "#e2e8f0", fontSize: 14, outline: "none", width: 280 },
  sortGroup: { display: "flex", gap: 6, alignItems: "center" },
  sortLabel: { fontSize: 12, color: "#64748b", marginRight: 4 },
  sortBtn: { background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#94a3b8", padding: "5px 12px", fontSize: 12, cursor: "pointer" },
  sortBtnActive: { background: "rgba(99,102,241,0.2)", border: "1px solid rgba(99,102,241,0.5)", color: "#a5b4fc" },
  infoBox: { margin: "12px 32px 0", background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 8, padding: "10px 16px", fontSize: 13, color: "#a5b4fc" },
  centered: { display: "flex", alignItems: "center", justifyContent: "center", padding: 80 },
  loadingText: { color: "#64748b", fontSize: 14 },
  errorBox: { margin: "24px 32px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, padding: "16px 20px", color: "#fca5a5", display: "flex", alignItems: "center", gap: 16 },
  retryBtn: { background: "rgba(239,68,68,0.2)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 6, color: "#fca5a5", padding: "5px 14px", cursor: "pointer", fontSize: 13 },
  tableWrap: { overflowX: "auto", padding: "0 32px" },
  emptyState: { padding: "48px 0", textAlign: "center", color: "#475569", fontSize: 14 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "10px 14px", color: "#475569", fontWeight: 500, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid rgba(255,255,255,0.06)", whiteSpace: "nowrap" },
  td: { padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.04)", verticalAlign: "middle" },
  stationName: { fontWeight: 500, color: "#f1f5f9" },
  statusBadge: { display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500, whiteSpace: "nowrap" },
  uptimeWrap: { display: "flex", alignItems: "center", gap: 8 },
  uptimeBar: { width: 60, height: 5, background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden" },
  uptimeFill: { height: "100%", borderRadius: 3, transition: "width 0.4s ease" },
  stat: { fontFamily: "monospace", fontSize: 13, color: "#e2e8f0" },
};